import { type CreateUpdateOrderDTO, CustomerType, type OrderDTO, type QliroOrder } from 'ecosystem'
import { roundNumber, sumArrayOfNumbers } from 'shared-utils'
import { createError } from '../../../../utils/fetching'
import { generateQliroAddresses } from '../utils/qliro'
import { type CompleteStorefrontOrderProps } from './types'
import {
  generateBasePayload,
  generatePayloadFromIngrid,
  generatePayloadFromNshift,
  generatePayloadFromPostNord
} from './utils'

const generateBasePayloadFromQliro = (
  oldOrder: OrderDTO,
  qliroOrder: QliroOrder
): CreateUpdateOrderDTO => {
  const {
    billingAddress,
    shippingAddress,
    customerInfo: { email, firstName, lastName }
  } = generateQliroAddresses(qliroOrder)

  const orderItems = qliroOrder.OrderItems

  const orderDiscount = orderItems.reduce((sum, item) => {
    return item.Type === 'Discount' ? sum + Math.abs(item.PricePerItemIncVat) : sum
  }, 0)

  const shippingCost = orderItems.reduce((sum, item) => {
    return item.Type === 'Shipping' ? sum + item.PricePerItemIncVat : sum
  }, 0)

  const orderAmount = qliroOrder.TotalPrice

  const orderTaxAmount = roundNumber(
    sumArrayOfNumbers(
      orderItems.map((item) => (item.PricePerItemIncVat - item.PricePerItemExVat) * item.Quantity)
    )
  )

  return {
    ...generateBasePayload(oldOrder),
    billingAddress,
    shippingAddress,
    currency: qliroOrder.Currency,
    customer: {
      email,
      firstName,
      lastName,
      type: CustomerType.CONSUMER
    },
    orderAmount,
    orderTaxAmount,
    orderDiscount: roundNumber(orderDiscount),
    shippingCost,

    paymentId: String(qliroOrder.OrderId),
    paymentProvider: 'QLIRO'
  }
}

export const qliroPaymentProvider = ({
  order: oldOrder,
  paymentProviderInfo,
  shippingProviderInfo
}: CompleteStorefrontOrderProps): CreateUpdateOrderDTO => {
  if (!paymentProviderInfo.providerResponse?.qliroResponse) {
    throw createError('Not found QLIRO order')
  }
  const shippingProviderName = shippingProviderInfo?.providerName ?? 'DEFAULT'

  let payload = generateBasePayloadFromQliro(
    oldOrder,
    paymentProviderInfo.providerResponse?.qliroResponse
  )

  switch (shippingProviderName) {
    case 'DEFAULT':
      break
    case 'POSTNORD':
      payload = generatePayloadFromPostNord(payload, shippingProviderInfo?.postnordResponse)
      break
    case 'INGRID':
      payload = generatePayloadFromIngrid(payload, shippingProviderInfo?.ingridResponse)
      break
    case 'NSHIFT':
      payload = generatePayloadFromNshift(payload, shippingProviderInfo?.nshiftResponse)
  }

  return payload
}
